import React, { useEffect, useState } from "react";
import { Checkbox, Form, Select } from "antd";
import { FormListFieldData } from "antd/lib/form/FormList";
import { PlusOutlined, MinusCircleOutlined } from "assets/icons";
import { IDocumentList } from "store/process/process-model";
import { documentInboundRules, documentOutboundRules, requiredDocumentOptions } from "utils/form/validations";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "./../styled/addDocument.styled";

export interface IAddDocument {
  fields: FormListFieldData[];
  setFieldsCount: React.Dispatch<React.SetStateAction<number>>;
  fieldsCount?: number;
  add: () => void;
  remove: (...args) => void;
  documentsList: IDocumentList[];
  eventOutboundActive: boolean;
  eventInboundActive: boolean;
  visibleDocuments: boolean;
  processUpdateVisibleDocuments: (...args) => void;
}

const AddDocuments = ({
  fields,
  setFieldsCount,
  fieldsCount,
  add,
  remove,
  documentsList,
  eventOutboundActive,
  eventInboundActive,
  processUpdateVisibleDocuments,
  visibleDocuments,
}: IAddDocument) => {
  const { Option } = Select;
  const INVALID_EVENT_MESSAGE = useTranslation({ id: "invalid_event" });
  const INBOUND = useTranslation({ id: "inbound" });
  const OUTBOUND = useTranslation({ id: "outbound" });
  const SELECT_DOCUMENT = useTranslation({ id: "select_document" });
  const SELECT_AN_DOCUMENT = useTranslation({ id: "select_an_option" });
  const [visibleCheckDocuments, setVisibleCheckDocuments] = useState(visibleDocuments);
  const isLastItem = (index) => index + 1 === fields.length;

  useEffect(() => {
    setFieldsCount(fields.length);
  }, [fields]);

  useEffect(() => {
    setVisibleCheckDocuments(visibleDocuments);
  }, [visibleDocuments]);

  const onChangeDocuments = (e) => {
    setVisibleCheckDocuments(e.target.checked);
    processUpdateVisibleDocuments(e.target.checked);
  };

  return (
    <>
      <S.CheckboxDocument>
        <Checkbox onChange={onChangeDocuments} checked={visibleCheckDocuments} data-testid="checkDocuments">
          {`${useTranslation({ id: "inform_document" })}`}
        </Checkbox>
      </S.CheckboxDocument>
      {visibleCheckDocuments && (
        <S.FormAddInvoices count={fieldsCount}>
          {fields.map((field, index) => {
            return (
              <React.Fragment key={index}>
                <S.DocumentContainer>
                  <Form.Item {...field} name={[field.name, "id"]} validateTrigger={["onChange", "onBlur"]} key={field.key} className="GridItens">
                    <Select size="large" style={S.InvoicesField} data-testid="selectDocument" placeholder={SELECT_DOCUMENT}>
                      {documentsList.map((x) => (
                        <Option key={x.id} value={x.id}>
                          {x.description.toUpperCase()}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item {...field} className="GridItens" name={[field.name, "required"]} validateTrigger={["onChange", "onBlur"]} key={`${field.key}-required`}>
                    <Select style={S.InvoicesField} size="large" data-testid="selectRequiredOptions" placeholder={SELECT_AN_DOCUMENT}>
                      {requiredDocumentOptions.map((x) => (
                        <Option value={x.id} key={x.id}>
                          {x.description}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, "documentInbound"]}
                    validateTrigger={["onChange", "onBlur"]}
                    valuePropName="checked"
                    key={`${field.key}-inbound`}
                    className="GridItens"
                    initialValue={false}
                    rules={[
                      {
                        validator: (_, value) => documentInboundRules(value, eventInboundActive, eventOutboundActive, INVALID_EVENT_MESSAGE),
                      },
                    ]}
                  >
                    <Checkbox data-testid="inboundCheckbox">{INBOUND}</Checkbox>
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, "documentOutbound"]}
                    validateTrigger={["onChange", "onBlur"]}
                    valuePropName="checked"
                    key={`${field.key}-outbound`}
                    className="GridItens"
                    initialValue={false}
                    rules={[
                      {
                        validator: (_, value) => documentOutboundRules(value, eventInboundActive, eventOutboundActive, INVALID_EVENT_MESSAGE),
                      },
                    ]}
                  >
                    <Checkbox data-testid="outboundCheckbox">{OUTBOUND}</Checkbox>
                  </Form.Item>
                </S.DocumentContainer>
                {fieldsCount > 1 && (
                  <S.RemoveButtonInvoice
                    data-testid="remove-btn"
                    onClick={() => {
                      remove(field.name);
                    }}
                    style={S.InvoicesFieldIcon}
                    icon={<MinusCircleOutlined className="removeButtonInvoices" />}
                  />
                )}
                {isLastItem(index) && (
                  <>
                    {fieldsCount > 1 && <div></div>}
                    <S.AddButtonInvoice
                      data-testid="add-btn"
                      onClick={() => {
                        add();
                        setFieldsCount(fieldsCount + 1);
                      }}
                      icon={<PlusOutlined className="addButtonInvoices" style={S.InvoicesFieldIcon} />}
                    ></S.AddButtonInvoice>
                  </>
                )}
              </React.Fragment>
            );
          })}
        </S.FormAddInvoices>
      )}
    </>
  );
};

export default AddDocuments;
